<template>
  <div
    class="sf-card cursor-pointer"
    :class="[`sf-card--${cardVariant}`, `sf-card--shape-${imageShape}`, { 'sf-card--no-image': !imageVisible, 'sf-card--has-add': addButtonVisible && !imageVisible }]"
    role="button"
    tabindex="0"
    @click="select"
    @keydown.enter="select"
  >
    <!-- Minimalista: cardápio impresso (nome ...... preço) -->
    <template v-if="cardVariant === 'minimal'">
      <div class="sf-card__line">
        <h5 class="sf-card__name">{{ product.name }}</h5>
        <span class="sf-card__leader" aria-hidden="true"></span>
        <span class="sf-card__prices">
          <template v-if="promo">
            <span class="sf-card__price sf-card__price--old">{{ formatBRL(product.price) }}</span>
            <span class="sf-card__price sf-card__price--promo">{{ formatBRL(product.promotionalPrice) }}</span>
          </template>
          <span v-else class="sf-card__price">{{ formatBRL(product.price) }}</span>
        </span>
      </div>
      <p v-if="descriptionVisible" class="sf-card__desc only-2-lines">{{ product.description }}</p>
      <span v-if="percentBadge" class="sf-card__badge sf-card__badge--inline">{{ product.discount }}% off</span>
      <button v-if="addButtonVisible" type="button" class="sf-card__add" :aria-label="`Adicionar ${product.name}`" @click.stop="select" @keydown.enter.stop>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/></svg>
      </button>
    </template>

    <template v-else>
      <div v-if="imageVisible" class="sf-card__media">
        <img
          :src="getProductImage(product, thumb)"
          :alt="product.name"
          loading="lazy"
          @error="($event.target as HTMLImageElement).src = '/assets/no-image.png'"
        >
        <div v-if="percentBadge" class="sf-card__badge">{{ product.discount }}% off</div>
        <button v-if="addButtonVisible" type="button" class="sf-card__add sf-card__add--on-media" :aria-label="`Adicionar ${product.name}`" @click.stop="select" @keydown.enter.stop>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/></svg>
        </button>
      </div>
      <div class="sf-card__body">
        <h5 class="sf-card__name">{{ product.name }}</h5>
        <p v-if="descriptionVisible" class="sf-card__desc only-2-lines">{{ product.description }}</p>
        <span v-if="percentBadge && !imageVisible" class="sf-card__badge sf-card__badge--inline">{{ product.discount }}% off</span>
        <div class="sf-card__prices">
          <template v-if="promo">
            <span class="sf-card__price sf-card__price--old"><span class="sf-card__price-label">De: </span><s>{{ formatBRL(product.price) }}</s></span>
            <span class="sf-card__price sf-card__price--promo"><span class="sf-card__price-label">Por: </span>{{ formatBRL(product.promotionalPrice) }}</span>
          </template>
          <span v-else class="sf-card__price">{{ formatBRL(product.price) }}</span>
        </div>
      </div>
      <button v-if="addButtonVisible && !imageVisible" type="button" class="sf-card__add" :aria-label="`Adicionar ${product.name}`" @click.stop="select" @keydown.enter.stop>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/></svg>
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getProductImage } from '@/global/state/GlobalState'
import { storefrontTemplate, storefrontShowImages, storefrontShowDescription, storefrontImageShape, storefrontShowAddButton, ProductCardVariant } from '@/global/state/StorefrontState'
import { formatBRL, hasPromotion } from './storefrontUtils'

/* global defineProps, defineEmits, withDefaults */
const props = withDefaults(defineProps<{
  product: any
  variant?: ProductCardVariant
  /** use the thumbnail media when available (Home / busca) */
  thumb?: boolean
}>(), { variant: undefined, thumb: true })

const emit = defineEmits(['select'])

const cardVariant = computed<ProductCardVariant>(() => props.variant ?? storefrontTemplate.value)
const imageVisible = computed(() => cardVariant.value !== 'minimal' && storefrontShowImages.value)
const imageShape = storefrontImageShape
/** "+" opens the same product modal as the card (products may have required add-ons). */
const addButtonVisible = computed(() => storefrontShowAddButton.value)
const descriptionVisible = computed(() => storefrontShowDescription.value && !!props.product?.description)
const promo = computed(() => hasPromotion(props.product))
const percentBadge = computed(() => props.product?.discountType == 'percent' && !!props.product?.discount)

function select() {
  emit('select', props.product)
}
</script>

<style>
.sf-card {
  position: relative;
  color: var(--sf-text);
  font-family: var(--sf-font);
  outline: none;
}
.sf-card:focus-visible {
  box-shadow: 0 0 0 2px var(--sf-primary);
}
.sf-card__media {
  position: relative;
  overflow: hidden;
  border-radius: var(--sf-radius);
  background: #f3f4f6;
  flex-shrink: 0;
}
.sf-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform .3s ease;
}
.sf-card:hover .sf-card__media img {
  transform: scale(1.04);
}
.sf-card__badge {
  position: absolute;
  top: 0;
  left: 0;
  padding: .3rem .55rem;
  font-size: .75rem;
  font-weight: 700;
  color: #fff;
  background: #f87171;
  border-bottom-right-radius: var(--sf-radius);
}
.sf-card__badge--inline {
  position: static;
  display: inline-block;
  margin-top: .35rem;
  border-radius: var(--sf-radius);
}
.sf-card__name {
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.3;
  color: var(--sf-text);
}
.sf-card__desc {
  color: var(--sf-muted);
  font-size: .8125rem;
  line-height: 1.4;
  margin-top: .25rem;
  overflow: hidden;
}
.sf-card__prices {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: .5rem;
  margin-top: .5rem;
}
.sf-card__price {
  font-weight: 700;
  font-size: .9375rem;
  color: var(--sf-price);
}
.sf-card__price--old {
  font-size: .8125rem;
  font-weight: 600;
  color: #f87171;
}
.sf-card__price--old .sf-card__price-label {
  color: var(--sf-muted);
}
.sf-card__price--promo {
  color: #16a34a;
}
.sf-card__price-label {
  font-weight: 600;
  color: var(--sf-text);
}

/* Clássico: linha (texto à esquerda, imagem quadrada à direita) */
.sf-card--classic {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--sf-border);
}
.sf-card--classic .sf-card__body {
  flex: 1;
  min-width: 0;
  order: 1;
}
.sf-card--classic .sf-card__media {
  order: 2;
  width: 6.5rem;
  height: 6.5rem;
}
@media (min-width: 768px) {
  .sf-card--classic .sf-card__media {
    width: 7.5rem;
    height: 7.5rem;
  }
}

/* Grade: imagem em cima, compacto */
.sf-card--grid .sf-card__media,
.sf-card--dark .sf-card__media {
  aspect-ratio: 1 / 1;
}
.sf-card--grid .sf-card__body {
  padding-top: .5rem;
}
.sf-card--grid .sf-card__name {
  font-size: .9rem;
}
.sf-card--grid .sf-card__price-label,
.sf-card--dark .sf-card__price-label {
  display: none;
}

/* Vitrine: imagem grande 4:3 */
.sf-card--showcase .sf-card__media {
  aspect-ratio: 4 / 3;
}
.sf-card--showcase .sf-card__body {
  padding-top: .875rem;
}
.sf-card--showcase .sf-card__name {
  font-size: 1.125rem;
}
.sf-card--showcase .sf-card__price {
  font-size: 1.0625rem;
}

/* Noturno: card escuro com destaque na cor primária */
.sf-card--dark {
  background: var(--sf-card-bg);
  border: 1px solid var(--sf-border);
  border-radius: var(--sf-radius);
  overflow: hidden;
  transition: border-color .2s, box-shadow .2s;
}
.sf-card--dark:hover {
  border-color: var(--sf-primary);
  box-shadow: 0 0 18px -4px var(--sf-primary);
}
.sf-card--dark .sf-card__media {
  border-radius: 0;
  background: #26262e;
}
.sf-card--dark .sf-card__body {
  padding: .75rem;
}
.sf-card--dark .sf-card__name {
  font-size: .9rem;
}
.sf-card--dark .sf-card__price,
.sf-card--dark .sf-card__price--promo {
  color: var(--sf-primary);
}
.sf-card--dark .sf-card__price--old {
  color: var(--sf-muted);
}

/* ---------- botão "+" ---------- */
.sf-card__add {
  position: absolute;
  right: 0;
  bottom: 1rem;
  z-index: 2;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--sf-primary);
  color: var(--sf-on-primary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, .18);
  transition: transform .15s ease, filter .15s ease;
}
.sf-card__add svg {
  width: 1.25rem;
  height: 1.25rem;
}
.sf-card__add:hover {
  transform: scale(1.08);
  filter: brightness(1.05);
}
.sf-card__add:focus-visible {
  outline: 2px solid var(--sf-primary);
  outline-offset: 2px;
}
.sf-card__add--on-media {
  right: .45rem;
  bottom: .45rem;
}
.sf-card--has-add .sf-card__body,
.sf-card--minimal.sf-card--has-add {
  padding-right: 2.75rem;
}
.sf-card--dark.sf-card--has-add .sf-card__add,
.sf-card--grid.sf-card--has-add .sf-card__add,
.sf-card--showcase.sf-card--has-add .sf-card__add {
  bottom: .5rem;
  right: .5rem;
}
.sf-card--minimal .sf-card__add {
  bottom: .9rem;
  width: 1.75rem;
  height: 1.75rem;
}

/* ---------- formato da imagem ---------- */
.sf-card--shape-square .sf-card__media {
  border-radius: 0;
}
.sf-card--shape-circle .sf-card__media {
  border-radius: 999px;
  overflow: visible;
  background: transparent;
}
.sf-card--shape-circle .sf-card__media img {
  border-radius: 999px;
  background: #f3f4f6;
}
.sf-card--shape-circle .sf-card__badge {
  border-radius: 999px;
}
/* cards com imagem em cima: círculo centralizado */
.sf-card--grid.sf-card--shape-circle .sf-card__media,
.sf-card--dark.sf-card--shape-circle .sf-card__media,
.sf-card--showcase.sf-card--shape-circle .sf-card__media {
  width: 78%;
  aspect-ratio: 1 / 1;
  margin: 0 auto;
}
.sf-card--showcase.sf-card--shape-circle .sf-card__media {
  width: 66%;
}
.sf-card--dark.sf-card--shape-circle .sf-card__media {
  margin-top: .875rem;
  background: transparent;
}
.sf-card--grid.sf-card--shape-circle .sf-card__body,
.sf-card--dark.sf-card--shape-circle .sf-card__body,
.sf-card--showcase.sf-card--shape-circle .sf-card__body {
  text-align: center;
}
.sf-card--grid.sf-card--shape-circle .sf-card__prices,
.sf-card--dark.sf-card--shape-circle .sf-card__prices,
.sf-card--showcase.sf-card--shape-circle .sf-card__prices {
  justify-content: center;
}
.sf-card--shape-circle .sf-card__add--on-media {
  right: 0;
  bottom: 0;
}

/* Minimalista */
.sf-card--minimal {
  padding: .9rem 0;
}
.sf-card__line {
  display: flex;
  align-items: baseline;
  gap: .5rem;
}
.sf-card--minimal .sf-card__name {
  font-family: var(--sf-heading-font);
  font-weight: 600;
  font-size: 1.05rem;
  flex-shrink: 1;
  min-width: 0;
}
.sf-card__leader {
  flex: 1;
  min-width: 1.5rem;
  border-bottom: 2px dotted var(--sf-border);
  transform: translateY(-.25rem);
}
.sf-card--minimal .sf-card__prices {
  margin-top: 0;
  flex-shrink: 0;
  flex-wrap: nowrap;
}
.sf-card--minimal .sf-card__price {
  font-family: var(--sf-heading-font);
  font-weight: 600;
}
.sf-card--minimal .sf-card__price--old {
  text-decoration: line-through;
  color: var(--sf-muted);
}
.sf-card--minimal .sf-card__price--promo {
  color: var(--sf-primary);
}
.sf-card--minimal .sf-card__desc {
  font-style: italic;
  max-width: 90%;
}
.sf-card--minimal:hover .sf-card__name {
  color: var(--sf-primary);
}
</style>
